import { useEffect, useRef, useState, type FormEvent } from 'react'
import { currencySymbol } from '@/lib/utils'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Camera, X } from 'lucide-react'
import { toast } from 'sonner'
import { useCreateSubmission, useMeta, useMyMachines, useWorkCenters } from '@/lib/queries'
import { ApiError } from '@/lib/api'
import { enqueue } from '@/lib/offline-queue'
import { ulid } from '@/lib/ulid'
import { machineFromQuery } from '@/lib/machinePicker'
import { useOnlineStatus } from '@/hooks/useOfflineQueue'
import { useAuth } from '@/context/AuthContext'
import { VoiceRecorder, type VoiceNote } from '@/components/VoiceRecorder'
import { Hint } from '@/components/Hint'
import { MachinePicker } from '@/components/submissions/MachinePicker'
import { Segmented, TypeTiles } from '@/components/submissions/ChoiceTiles'
import {
  Button,
  Card,
  DateInput,
  Field,
  Input,
  NumberInput,
  Textarea,
} from '@/components/ui'
import { useT } from '@/i18n/I18nProvider'

const MAX_FILES = 10

/**
 * A new report, in the order a person at a machine thinks of it: which
 * machine, what kind of problem, a few words — and, if they like, a
 * description, a photo or a voice message.
 *
 * Built for a phone held in a glove. The machine usually arrives already
 * chosen, from a sticker or a link; the type is a grid of large tiles rather
 * than a dropdown; and nothing below the title is required. Offline is a
 * normal state: the report is queued on the device and sent when the signal
 * comes back (lib/offline-queue.ts), so the same button works in the corner
 * of the plant the wifi does not reach.
 */
export function NewSubmissionPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const t = useT()
  const isOnline = useOnlineStatus()
  const meta = useMeta()
  const workCenters = useWorkCenters()
  const myMachines = useMyMachines()
  const create = useCreateSubmission()

  const [type, setType] = useState('INCIDENT')
  // Pre-filled when the user arrived by scanning the QR code on a machine, or
  // from a link that names one.
  const [workCenterId, setWorkCenterId] = useState(machineFromQuery(searchParams))
  const [priority, setPriority] = useState('NORMAL')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const machineRef = useRef<HTMLDivElement>(null)

  /*
   * The spoken message. One per report, deliberately.
   *
   * For a good part of this workforce it is the fastest and most likely route
   * from noticing a fault to reporting one — saying it takes four seconds,
   * typing it on a phone with gloves on takes a minute, and plenty of people
   * simply will not do the second one.
   */
  const [voiceNote, setVoiceNote] = useState<VoiceNote | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const [improvement, setImprovement] = useState({
    currentState: '',
    proposal: '',
    expectedImpact: '',
    estimatedDuration: '',
    possibleDate: '',
    estimatedSavings: '',
    estimatedCost: '',
  })

  useEffect(() => {
    const fromQuery = machineFromQuery(searchParams)
    if (fromQuery) setWorkCenterId(fromQuery)
  }, [searchParams])

  const isImprovement = type === 'IMPROVEMENT'

  function addFiles(list: FileList | null) {
    if (!list) return
    setFiles((current) => [...current, ...Array.from(list)].slice(0, MAX_FILES))
  }

  /** Everything needed to send this submission, now or later from the queue. */
  function collectPayload() {
    return {
      workCenterId,
      type,
      priority,
      title: title || undefined,
      description: description || undefined,
      improvement: isImprovement ? improvement : undefined,
    }
  }

  /**
   * The one thing the form cannot do without, checked before anything is sent
   * or queued — offline there is no server to say so, and a queued report with
   * no machine would fail hours later, out of sight.
   */
  function machineMissing(): boolean {
    if (workCenterId) return false

    setErrors({ workCenterId: t('submissions.form.machine.required') })
    toast.error(t('submissions.form.machine.required'))
    machineRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })

    return true
  }

  /**
   * Save to the device and leave.
   *
   * Used when there is no connection, and as the fallback when a send fails for
   * a network reason. A worker who has walked to a machine, photographed a
   * fault and typed it up must never lose that because the wifi did not reach
   * that corner of the plant.
   */
  async function saveForLater(reason: 'offline' | 'failed') {
    /*
     * Mirror the server's "must carry something" rule before queueing.
     *
     * Offline there is no server to reject an empty report, so without this
     * check it would sit in the queue, fail with a 422 when the signal returns,
     * and confront the worker with an error about something they typed hours
     * earlier. Catching it now costs one line and keeps the queue clean.
     */
    const hasContent = Boolean(description || title || files.length > 0 || voiceNote)

    if (!hasContent && !isImprovement) {
      setErrors({ description: t('submissions.form.needContent') })
      toast.error(t('submissions.form.needContent.toast'))
      return
    }

    try {
      await enqueue({
        userId: user!.id,
        payload: collectPayload(),
        files,
        voiceNote,
        workCenterName:
          workCenters.data?.find((center) => center.id === workCenterId)?.name ?? '',
        typeLabel:
          meta.data?.submissionTypes.find((option) => option.value === type)?.label ?? type,
      })

      toast.success(
        t(
          reason === 'offline'
            ? 'submissions.form.savedOffline'
            : 'submissions.form.savedAfterFailure',
        ),
      )

      navigate('/prijave', { replace: true })
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('submissions.form.saveFailed'))
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setErrors({})

    if (machineMissing()) return

    // Offline is a normal state here, not an error. Skip the doomed request.
    if (!isOnline) {
      await saveForLater('offline')
      return
    }

    const payload = collectPayload()
    const form = new FormData()

    // The idempotency key is minted before the first attempt, so if this
    // request succeeds but its response is lost, the retry from the queue
    // resolves to the same submission rather than creating a second one.
    form.append('clientUuid', ulid())
    form.append('workCenterId', payload.workCenterId)
    form.append('type', payload.type)
    form.append('priority', payload.priority)
    if (payload.title) form.append('title', payload.title)
    if (payload.description) form.append('description', payload.description)

    if (payload.improvement) {
      for (const [key, value] of Object.entries(payload.improvement)) {
        if (value) form.append(`improvement[${key}]`, value)
      }
    }

    files.forEach((file) => form.append('files[]', file))

    if (voiceNote) {
      form.append('voiceNote', voiceNote.file)
      // Counted by the recorder, because the file itself cannot be asked — a
      // MediaRecorder writes no duration into its own WebM header.
      form.append('voiceNoteDurationSec', String(voiceNote.durationSec))
    }

    try {
      const submission = await create.mutateAsync(form)
      toast.success(t('submissions.form.sent', { reference: submission.referenceNo }))
      navigate(`/prijave/${submission.id}`, { replace: true })
    } catch (error) {
      // A network failure means the report is still good — queue it.
      if (error instanceof ApiError && error.isOffline) {
        await saveForLater('failed')
        return
      }

      if (error instanceof ApiError) {
        // Flatten Laravel's field errors so each input can show its own message.
        setErrors(
          Object.fromEntries(Object.entries(error.errors).map(([key, list]) => [key, list[0]])),
        )
        toast.error(error.message)
      } else {
        toast.error(t('submissions.form.sendFailed'))
      }
    }
  }

  return (
    <div className="space-y-4">
      <header>
        {/*
          The two facts nobody finds out on their own — that a missing signal
          is no reason to walk back to the office, and that what comes back is
          a number to quote on a work order — on the heading's question mark.
          They were a paragraph above the form, which on a phone pushed the
          first field below the fold.
        */}
        <h1 className="flex items-center gap-2 text-xl font-semibold sm:text-2xl">
          {t('submissions.form.heading')}
          <Hint side="bottom">{t('submissions.form.help')}</Hint>
        </h1>
        <p className="muted text-sm">{t('submissions.form.subtitle')}</p>
      </header>

      {/*
        One column up to xl, two beyond it: the questions on the left, the
        evidence and the button on the right, pinned so "Pošalji" stays in
        view however long the improvement section grows.
      */}
      <form
        onSubmit={handleSubmit}
        // `grid-cols-1` is `minmax(0, 1fr)`: an implicit track grows to the
        // widest tile label, which on a 375px phone was five pixels too many.
        className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]"
      >
        <div className="space-y-4">
          <Card className="space-y-3">
            <div ref={machineRef} className="scroll-mt-24">
              <h2 className="mb-2 flex items-center gap-1.5 font-medium">
                {t('submissions.form.machine.heading')}
                <Hint>{t('submissions.form.workCenter.hint')}</Hint>
              </h2>
              <MachinePicker
                centres={workCenters.data}
                hints={myMachines.data}
                value={workCenterId}
                onChange={(id) => {
                  setWorkCenterId(id)
                  setErrors((current) => {
                    const next = { ...current }
                    delete next.workCenterId
                    return next
                  })
                }}
                error={errors.workCenterId}
                isOnline={isOnline}
                loading={workCenters.isLoading}
              />
            </div>
          </Card>

          <Card className="space-y-4">
            {/* The question mark says what the choice *does*: IMPROVEMENT is
                the one type that changes the form underneath you. */}
            <TypeTiles
              name="type"
              options={meta.data?.submissionTypes ?? []}
              value={type}
              onChange={setType}
              legend={
                <>
                  {t('submissions.form.type')}
                  <Hint>{t('submissions.form.type.hint')}</Hint>
                </>
              }
            />
            {errors.type && (
              <p role="alert" className="text-xs font-medium text-rose-600">
                {errors.type}
              </p>
            )}

            <Segmented
              name="priority"
              options={meta.data?.priorities ?? []}
              value={priority}
              onChange={setPriority}
              legend={
                <>
                  {t('submissions.form.priority')}
                  <Hint>{t('submissions.form.priority.hint')}</Hint>
                </>
              }
            />
          </Card>

          <Card className="space-y-4">
            <Field
              label={t('submissions.form.title')}
              error={errors.title}
              hint={t('submissions.form.title.hint')}
            >
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={255}
                placeholder={t('submissions.form.title.placeholder')}
                enterKeyHint="next"
              />
            </Field>

            <Field label={t('submissions.form.description')} error={errors.description}>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                maxLength={5000}
                placeholder={t('submissions.form.description.placeholder')}
              />
            </Field>
          </Card>

          {isImprovement && (
            <Card className="space-y-4">
              <h2 className="font-medium">{t('submissions.form.improvement.heading')}</h2>

              <Field
                label={t('submissions.improvement.currentState')}
                error={errors['improvement.currentState']}
              >
                <Textarea
                  value={improvement.currentState}
                  onChange={(e) => setImprovement((c) => ({ ...c, currentState: e.target.value }))}
                  rows={3}
                  placeholder={t('submissions.form.improvement.currentState.placeholder')}
                />
              </Field>

              <Field
                label={t('submissions.improvement.proposal')}
                required
                error={errors['improvement.proposal']}
              >
                <Textarea
                  value={improvement.proposal}
                  onChange={(e) => setImprovement((c) => ({ ...c, proposal: e.target.value }))}
                  rows={4}
                  required
                  placeholder={t('submissions.form.improvement.proposal.placeholder')}
                />
              </Field>

              <Field
                label={t('submissions.improvement.expectedImpact')}
                error={errors['improvement.expectedImpact']}
              >
                <Textarea
                  value={improvement.expectedImpact}
                  onChange={(e) => setImprovement((c) => ({ ...c, expectedImpact: e.target.value }))}
                  rows={3}
                  placeholder={t('submissions.form.improvement.expectedImpact.placeholder')}
                />
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t('submissions.form.improvement.duration')}>
                  <Input
                    value={improvement.estimatedDuration}
                    onChange={(e) =>
                      setImprovement((c) => ({ ...c, estimatedDuration: e.target.value }))
                    }
                    placeholder={t('submissions.form.improvement.duration.placeholder')}
                  />
                </Field>

                <Field label={t('submissions.form.improvement.possibleDate')}>
                  <DateInput
                    value={improvement.possibleDate}
                    onChange={(e) => setImprovement((c) => ({ ...c, possibleDate: e.target.value }))}
                  />
                </Field>

                {/* One hint for both amounts, on the first of them. The same
                    sentence twice in a two-column grid reads as two rules. */}
                <Field
                  label={t('submissions.form.improvement.estimatedSavings', { currency: currencySymbol() })}
                  action={<Hint>{t('submissions.form.improvement.estimate.hint')}</Hint>}
                >
                  <NumberInput
                    min="0"
                    step="0.01"
                    placeholder="0,00"
                    value={improvement.estimatedSavings}
                    onChange={(e) =>
                      setImprovement((c) => ({ ...c, estimatedSavings: e.target.value }))
                    }
                  />
                </Field>

                <Field label={t('submissions.form.improvement.estimatedCost', { currency: currencySymbol() })}>
                  <NumberInput
                    min="0"
                    step="0.01"
                    placeholder="0,00"
                    value={improvement.estimatedCost}
                    onChange={(e) => setImprovement((c) => ({ ...c, estimatedCost: e.target.value }))}
                  />
                </Field>
              </div>
            </Card>
          )}
        </div>

        <div className="space-y-4 xl:sticky xl:top-6">
          {/*
            The photo and the voice message side by side in one card: both are
            evidence, both are optional, and for a fair number of people here
            one of them *is* the report — describing a fault out loud is faster
            than typing it and needs no free hand for the keyboard.
          */}
          <Card className="space-y-4">
            <div className="space-y-2">
              <p className="flex items-center gap-1.5 text-sm font-medium">
                {t('submissions.form.media.heading')}
                <Hint>{t('submissions.form.media.hint')}</Hint>
              </p>

              <label className="flex min-h-20 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[var(--border-subtle)] p-4 transition-colors hover:bg-[var(--surface-sunken)] has-focus-visible:ring-3 has-focus-visible:ring-ring/50">
                <Camera className="muted size-6" aria-hidden />
                <span className="muted text-sm">{t('submissions.form.media.add')}</span>
                <input
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  // capture opens the camera directly on a phone rather than the
                  // file picker — the fastest path from seeing a fault to reporting it.
                  capture="environment"
                  className="sr-only"
                  onChange={(e) => addFiles(e.target.files)}
                />
              </label>

              {files.length > 0 && (
                <ul className="space-y-2">
                  {files.map((file, index) => (
                    <li
                      key={`${file.name}-${index}`}
                      className="flex items-center justify-between gap-2 rounded-xl bg-[var(--surface-sunken)] px-3 py-2 text-sm"
                    >
                      <span className="truncate">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => setFiles(files.filter((_, i) => i !== index))}
                        aria-label={t('submissions.form.media.remove', { name: file.name })}
                        className="muted size-control flex shrink-0 items-center justify-center rounded-lg"
                      >
                        <X className="size-4" aria-hidden />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {errors['files.0'] && (
                <p role="alert" className="text-xs font-medium text-rose-600">
                  {errors['files.0']}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <p className="flex items-center gap-1.5 text-sm font-medium">
                {t('submissions.voice.label')}
                <Hint>{t('submissions.form.voice.explain')}</Hint>
              </p>
              <VoiceRecorder value={voiceNote} onChange={setVoiceNote} disabled={create.isPending} />

              {errors.voiceNote && (
                <p role="alert" className="text-xs font-medium text-rose-600">
                  {errors.voiceNote}
                </p>
              )}
            </div>
          </Card>

          <div className="flex gap-3">
            {/* The label tells the truth about what will happen. Promising "sent"
                while offline and then not sending is how a worker stops trusting
                the app. */}
            <Button type="submit" loading={create.isPending} fullWidth>
              {t(isOnline ? 'submissions.form.submit' : 'submissions.form.saveToDevice')}
            </Button>
            <Button type="button" variant="secondary" onClick={() => navigate(-1)}>
              {t('submissions.cancel')}
            </Button>
          </div>

          {!isOnline && (
            <p className="rounded-xl bg-slate-100 px-3 py-2.5 text-xs dark:bg-slate-800">
              {t('submissions.form.offlineNotice')}
            </p>
          )}
        </div>
      </form>
    </div>
  )
}
